// src/main.js
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { auth } from '@/firebaseConfig';
import { fetchPublishedExpressions, fetchPublishedMasterclasses, fetchEngineContent } from '@/api/appDataService';
import {
  readMirrorCache, writeMirrorCache, sameMirrorPayload,
} from '@/data/mirrorCache';
import { hydrateLibraryFixtures } from '@/data/libraryFixtures';
import { hydrateMasterclasses } from '@/data/masterclasses';
import { hydrateEngineContent } from '@/services/engineContent';
import { handOffSignedOut } from '@/utils/kakaoInApp';
import { keepSignedOutVisit } from '@/utils/challengeReturn';

// Theme tokens — light + dark CSS custom properties. Loaded globally so every
// surface can consume var(--token). The inline boot script in index.html has
// already set data-theme before this runs.
import '@/styles/theme.css';

// Vuetify
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

// Benign media rejections — the native Picture-in-Picture button pressed before
// the video is ready, or play() denied/interrupted by the platform — surface as
// uncaught promise rejections (NotAllowedError / AbortError). The browser owns the
// PiP request, so the app can't .catch() it at the source; swallow just these two
// so they don't trip the dev error overlay or spam the console. Real errors still
// surface unchanged.
window.addEventListener('unhandledrejection', (event) => {
  const name = event?.reason?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') {
    event.preventDefault();
  }
});

// A challenge link opened inside KakaoTalk by someone not signed in there goes
// to the phone's browser (the in-app browser has its own sign-in). Asked before
// the app mounts, so the link is still the one that was tapped.
handOffSignedOut(auth);
// And in any browser, a signed-out visitor's challenge link waits on the device
// for the 챌린지 tab to take up after sign-in, which does not remember it.
keepSignedOutVisit(auth);

let app;
let booting = false;

const vuetify = createVuetify({
  components,
  directives
});

// ── Boot data, cache-first (2026-08-01) ─────────────────────────────────────
// The mount used to be GATED on the Firestore mirror fetch — 2-6s of white
// screen on every load, protecting first paint from stale data. The trade now
// runs the other way (Eddie's call): hydrate INSTANTLY from the last visit's
// cached copy, mount, and reconcile in the background — a briefly-old number
// that visibly self-corrects beats a white screen. Offline degrades to stale
// (cached, or baked JSON on a first visit), never to broken.
// See docs/airtable-runtime-mirror-design.md for the mirror itself.

// 1. Synchronous, pre-mount: last visit's copy. localStorage + parse ≈ ms.
{
  const cachedExpr = readMirrorCache('expressions');
  const cachedMc = readMirrorCache('masterclasses');
  const cachedEngine = readMirrorCache('engineContent');
  if (cachedExpr) hydrateLibraryFixtures(cachedExpr);
  if (cachedMc) hydrateMasterclasses(cachedMc);
  if (cachedEngine) hydrateEngineContent(cachedEngine);
}

// 2. Background: fetch fresh, and only touch the stores if it actually
//    differs — reconciliation bumps mirrorTick, which the keep-alive'd home
//    subscribes to (every other page remounts and re-reads on its own).
async function hydrateAppData() {
  // The expressions mirror doc is ~190KB and routinely needs 2s+ to fetch, so the
  // old shared 1500ms timeout dropped it every time and left the stale baked JSON
  // (masterclasses, ~3KB, still made it — which is why only expression refreshes
  // never went live). Give the read a realistic budget and time-box each dataset
  // on its own so the small doc is never held hostage to the large one.
  const TIMEOUT_MS = 6000;
  const withTimeout = (p) =>
    Promise.race([p, new Promise((resolve) => setTimeout(() => resolve(null), TIMEOUT_MS))]);
  const [expressions, masterclasses, engineContent] = await Promise.all([
    withTimeout(fetchPublishedExpressions()).catch(() => null),
    withTimeout(fetchPublishedMasterclasses()).catch(() => null),
    withTimeout(fetchEngineContent()).catch(() => null),
  ]);
  if (Array.isArray(expressions) && expressions.length) {
    if (!sameMirrorPayload(expressions, readMirrorCache('expressions'))) {
      hydrateLibraryFixtures(expressions);
    }
    writeMirrorCache('expressions', expressions);
  }
  if (Array.isArray(masterclasses) && masterclasses.length) {
    if (!sameMirrorPayload(masterclasses, readMirrorCache('masterclasses'))) {
      hydrateMasterclasses(masterclasses);
    }
    writeMirrorCache('masterclasses', masterclasses);
  }
  if (engineContent && Array.isArray(engineContent.expressions)) {
    if (!sameMirrorPayload(engineContent, readMirrorCache('engineContent'))) {
      hydrateEngineContent(engineContent);
    }
    writeMirrorCache('engineContent', engineContent);
  }
}

// Start the mirror fetch NOW, not inside the auth callback. Hydrate and auth
// used to run in series — wait ~1s for Firebase auth, then start a 2s+
// Firestore read — so every load paid both, back to back.
//
// Safe to fire before the callback because the Firestore SDK holds its first
// RPC until the initial auth token resolves: a signed-in member's early read
// carries their token and succeeds, exactly as it did inside the callback.
// (appData reads REQUIRE sign-in per rules — a signed-out visitor gets
// permission-denied → catch → baked JSON, the same fallback as before.)
// The callback then awaits whatever is left of the fetch instead of all of it.
hydrateAppData(); // fire-and-forget: reconciliation, not a mount gate

auth.onAuthStateChanged(async () => {
  if (app || booting) return;
  booting = true;
  app = createApp(App);
  app.use(router);
  app.use(vuetify);
  app.mount('#app');
});
